import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getProfileData,
  getEducationData,
  getExperienceData,
  getSkillData,
} from "../../../service/api";
import { format } from "date-fns";

function ResumePrint() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [skills, setSkills] = useState([]);
  const [experiences, setExperiences] = useState([]);
  const [education, setEducation] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchData() {
      try {
        const [profileRes, skillsRes, expRes, eduRes] = await Promise.all([
          getProfileData(),
          getSkillData(),
          getExperienceData(),
          getEducationData(),
        ]);
        setProfile(profileRes.data);
        setSkills(skillsRes.data?.skillsList || []);
        setExperiences(expRes.data || []);
        setEducation(eduRes.data || []);
      } catch (error) {
        console.error("Error fetching profile data", error);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  const formatDate = (dateString) => {
    try {
      return format(
        new Date(dateString.split("-").reverse().join("-")),
        "MMM yyyy"
      );
    } catch {
      return dateString;
    }
  };

  useEffect(() => {
    if (!loading) {
      // Trigger print after component is loaded
      setTimeout(() => {
        window.print();
      }, 500);

      // After print or cancel, navigate back
      const afterPrintHandler = () => {
        navigate(-1); // go back to previous page
      };
      window.addEventListener("afterprint", afterPrintHandler);

      return () => {
        window.removeEventListener("afterprint", afterPrintHandler);
      };
    }
  }, [loading, navigate]);

  if (loading) return <div className="loader">Loading Resume...</div>;

  return (
    <div className="resume-container">
      {profile && (
        <div className="resume-header d-flex justify-content-between align-items-center">
          <div>
            <h1>{profile.name}</h1>
            <p>{profile.email}</p>
            <p>+91 {profile.phoneNumber}</p>
            <p>
              {profile.city}, {profile.state}, India
            </p>
          </div>
          <div>
            <img
              src={profile.image || "/assets/img/company-profiles/image-1.png"}
              alt="Profile"
              style={{ width: "120px", borderRadius: "50%" }}
            />
          </div>
        </div>
      )}

      {/* Skills */}
      <div className="resume-section">
        <h2 className="resume-title">Skills</h2>
        {skills.length > 0 ? (
          <div className="skill-list">
            {skills.map((skill, i) => (
              <span key={i} className="skill-badge">
                {skill.name}
              </span>
            ))}
          </div>
        ) : (
          <p className="empty-msg">No skills found.</p>
        )}
      </div>

      {/* Education */}
      <div className="resume-section-edu">
        <h2 className="resume-title">Education</h2>
        {education.length > 0 ? (
          education.map((edu, i) => (
            <div className="resume-card-edu" key={i}>
              <h5>{edu.degree}</h5>
              <h6 className="text-muted">{edu.boardOfUniversity}</h6>
              <p className="duration-edu">
                {formatDate(edu.sessionFrom)} – {formatDate(edu.sessionTo)}
              </p>
              <p className="duration-edu">
                Grade / Percentage: {edu.gradeOrPercentage}%
              </p>
            </div>
          ))
        ) : (
          <p className="empty-msg">No education found.</p>
        )}
      </div>

      {/* Experience */}
      <div className="resume-section">
        <h2 className="resume-title">Work Experience</h2>
        {experiences.length > 0 ? (
          experiences.map((exp, i) => (
            <div className="resume-card" key={i}>
              <h5>{exp.jobTitle}</h5>
              <h6 className="text-muted">{exp.companyName}</h6>
              <p className="duration">
                {formatDate(exp.sessionFrom)} – {formatDate(exp.sessionTo)}
              </p>
              <p>{exp.roleDescription}</p>
            </div>
          ))
        ) : (
          <p className="empty-msg">No work experience found.</p>
        )}
      </div>
    </div>
  );
}

export default ResumePrint;
